import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Loader2, QrCode, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';
import { MEMORIAL_COPY } from '../../utils/memorial';

const API = getApiBase();

const DEST_TYPES = [
  { value: 'memorial_page', label: 'Memorial page' },
  { value: 'story', label: 'Selected life story' },
  { value: 'audiobook', label: 'Audio keepsake (landing)' },
  { value: 'tribute_book', label: 'Tribute book (landing)' },
];

export function MemorialQRPanel({ personId, canManage, stories = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [destType, setDestType] = useState('memorial_page');
  const [storyId, setStoryId] = useState('');

  const fetchItems = useCallback(async () => {
    if (!personId || !canManage) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/individuals/${personId}/memorial/qr`);
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      toast.error('Could not load Memorial QR links');
    } finally {
      setLoading(false);
    }
  }, [personId, canManage]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createQr = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/individuals/${personId}/memorial/qr`, {
        destination_type: destType,
        target_id: destType === 'story' ? storyId || null : null,
      });
      toast.success('Memorial QR created');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create Memorial QR');
    } finally {
      setCreating(false);
    }
  };

  const downloadPng = async (qrId) => {
    try {
      const res = await axios.get(`${API}/individuals/${personId}/memorial/qr/${qrId}/png`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'memorial-qr.png';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download QR image');
    }
  };

  const revoke = async (qrId) => {
    if (!window.confirm('Revoke this Memorial QR? Scans will no longer work.')) return;
    try {
      await axios.delete(`${API}/individuals/${personId}/memorial/qr/${qrId}`);
      toast.success('Memorial QR revoked');
      fetchItems();
    } catch {
      toast.error('Could not revoke QR');
    }
  };

  if (!canManage) return null;

  return (
    <section className="rounded-xl border border-border/50 bg-muted/10 p-4 space-y-4" data-testid="memorial-qr-panel">
      <div className="flex items-center gap-2">
        <QrCode className="h-4 w-4 text-[#8B7355]" aria-hidden />
        <p className="text-sm font-medium">{MEMORIAL_COPY.memorialQr}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Creates a MyLegacyBridge memorial QR link you can reuse on printed items. Each code uses a token redirect — not a fixed page URL — so destinations can be managed from here.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs text-muted-foreground">Destination</Label>
          <Select value={destType} onValueChange={setDestType}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DEST_TYPES.map((d) => (
                <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {destType === 'story' ? (
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs text-muted-foreground">Story</Label>
            <Select value={storyId || undefined} onValueChange={setStoryId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Choose story" />
              </SelectTrigger>
              <SelectContent>
                {stories.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.display_title || s.question_text || 'Story'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
        <Button type="button" size="sm" className="rounded-full shrink-0" disabled={creating} onClick={createQr}>
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create QR'}
        </Button>
      </div>
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">QR links can be created after you publish — they open only the memorial page.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/40 bg-background/60 px-3 py-2 text-xs">
              <div className="min-w-0">
                <p className="font-medium">{item.label}</p>
                <p className="truncate font-mono text-muted-foreground">{item.target_url}</p>
              </div>
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => downloadPng(item.id)}>
                  <Download className="h-3.5 w-3.5 mr-1" /> PNG
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => revoke(item.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default MemorialQRPanel;
